import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import OptimizedImage from '@/components/ui/optimized-image';

const FloatingPill = ({ children, color, delay, top, left, right, bottom }: { children: React.ReactNode, color: string, delay: number, top?: string, left?: string, right?: string, bottom?: string }) => (
  <motion.div
    initial={{ y: 0 }}
    animate={{ y: [0, -15, 0] }}
    transition={{
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
    className={`absolute px-4 py-2 rounded-full text-xs lg:text-sm font-medium shadow-lg whitespace-nowrap ${color}`}
    style={{ top, left, right, bottom }}
  >
    {children}
  </motion.div>
);

const HeroSection = () => {
  const logos = [
    { name: 'Shiprocket', src: '/lovable-uploads/Shiprocket.png' },
    { name: 'Paytm', src: '/lovable-uploads/Paytm.png' },
    { name: 'Sprinto', src: '/lovable-uploads/Sprinto.png' },
    { name: 'Averlon', src: '/lovable-uploads/Averlon.png' },
  ];

  return (
    <section className="relative pt-32 lg:pt-40 pb-24 overflow-hidden bg-white">
      <div className="container px-4 mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              Find talent that <span className="italic font-bold text-cs-primary">belongs</span> in your startup
            </h1>
            <h2 className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
              Cutshort brings the right talent + tools to help you find engineering talent with right skills and attitude to make your startup a success.
            </h2>
            
            <div className="hidden lg:flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-12">
              <a 
                href="https://cutshort.io/?auth=employer" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-14 px-8 text-lg rounded-full bg-cs-primary text-white font-semibold hover:bg-cs-primary/90 transition-colors inline-flex items-center justify-center"
              >
                Start free trial
              </a>
            </div>

            {/* Trust Layer */}
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <div className="flex -space-x-3">
                  {[
                    { name: 'Gautam', src: '/lovable-uploads/Gautam-Ai-sensy.jpeg' },
                    { name: 'Saurabh', src: '/lovable-uploads/Saurabh-deepintent.jpeg' },
                    { name: 'Mukul', src: '/lovable-uploads/Mukul-Classplus.jpeg' },
                    { name: 'Apaksh', src: '/lovable-uploads/Apaksh-oneimpression.jpeg' }
                  ].map((founder, i) => (
                    <Avatar key={i} className="border-2 border-white w-10 h-10 shadow-sm">
                      <AvatarImage src={founder.src} alt={founder.name} />
                      <AvatarFallback className="bg-cs-primary/10 text-cs-primary text-xs font-bold">{founder.name[0]}</AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <p className="text-sm font-medium text-gray-600">
                  Loved by 5000+ founders
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 opacity-60 grayscale hover:grayscale-0 transition-all duration-300">
                {logos.map((logo) => (
                  <div key={logo.name} className="h-8">
                    <OptimizedImage 
                      src={logo.src} 
                      alt={logo.name} 
                      className="h-full w-auto object-contain"
                    />
                  </div>
                ))}
                <div className="h-12 border-l border-gray-200 mx-2 hidden sm:block"></div>
                <a 
                  href="https://www.g2.com/products/cutshort/reviews" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <OptimizedImage 
                    src="/lovable-uploads/eff70dcb-ba99-4c57-b3c6-7b58b91dff38.png" 
                    alt="G2" 
                    className="h-6 w-auto" 
                  />
                  <span className="text-xs font-bold text-gray-500">4.6 on G2</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Illustration */}
          <div className="flex-1 relative h-[450px] lg:h-[500px] w-full max-w-[500px]">
            <div className="absolute inset-0 bg-blue-50/50 rounded-full blur-3xl -z-10"></div>
            
            <FloatingPill color="bg-blue-100 text-blue-700" delay={0} top="15%" left="20%">
              Fast career growth
            </FloatingPill>
            
            <FloatingPill color="bg-green-100 text-green-700" delay={0.5} top="30%" right="10%">
              Early stage hire at Sprinto
            </FloatingPill>
            
            <FloatingPill color="bg-purple-100 text-purple-700" delay={1} top="50%" left="10%">
              Shipped Gen-AI features
            </FloatingPill>
            
            <FloatingPill color="bg-orange-100 text-orange-700" delay={1.5} top="65%" right="15%">
              Stable job tenures
            </FloatingPill>
            
            <FloatingPill color="bg-pink-100 text-pink-700" delay={2} top="80%" left="25%">
              Within budget &lt; 24 LPA
            </FloatingPill>

            {/* Central Illustration Placeholder/Graphic */}
            <div className="absolute inset-0 flex items-center justify-center -z-20">
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="w-48 h-48 lg:w-64 lg:h-64 border-2 border-dashed border-gray-200 rounded-full"
              ></motion.div>
              <motion.div 
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute w-36 h-36 lg:w-48 lg:h-48 border-2 border-dashed border-gray-100 rounded-full"
              ></motion.div>
            </div>
          </div>
        </div>

        {/* Mobile CTA */}
        <div className="flex lg:hidden flex-col items-center justify-center mt-12">
          <a 
            href="https://cutshort.io/?auth=employer" 
            target="_blank" 
            rel="noopener noreferrer"
            className="h-14 px-8 text-lg rounded-full bg-cs-primary text-white font-semibold hover:bg-cs-primary/90 transition-colors inline-flex items-center justify-center"
          >
            Start free trial
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
